/**
 * Canonical site-wide constants (NAP, nav, footer).
 *
 * The original site duplicated the nav and footer markup across all 8 pages.
 * Defining them once here is the single biggest structural win of the port:
 * a nav change is now one edit instead of eight.
 */

export const SITE = {
  name: 'Lux Financial',
  origin: 'https://luxfinancial.io',
  phone: '(877) 619-7804',
  phoneHref: 'tel:+18776197804',
  clientLogin: 'https://app.luxfinancial.io',
  mapsUrl: 'https://maps.app.goo.gl/AcAeagmW8V9p7hYb8',
  linkedin: 'https://www.linkedin.com/company/lux-financial',
  logo: 'images/lux-logo.png',
  googleAnalyticsId: 'G-CSX5NMVNPF',
  address: {
    street: '3301 N Thanksgiving Way, Suite 475',
    locality: 'Lehi',
    region: 'UT',
    postalCode: '84048',
    country: 'US',
  },
} as const;

export interface NavItem {
  readonly label: string;
  readonly href: string;
}

/** Primary nav. `href` values are absolute site paths (extensionless). */
export const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Home', href: '/' },
  { label: 'Solutions', href: '/solutions' },
  { label: 'About Us', href: '/about' },
  { label: 'Newsroom', href: '/newsroom' },
  { label: 'Data Room', href: '/data-room' },
] as const;

export const FOOTER_COLUMNS = [
  {
    heading: 'Company',
    links: [
      { label: 'Home', href: '/' },
      { label: 'Solutions', href: '/solutions' },
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/#contact' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'News Room', href: '/newsroom' },
      { label: 'Data Room', href: '/data-room' },
      { label: 'Login', href: SITE.clientLogin, external: true },
    ],
  },
] as const;

export const POLICY_LINKS = [
  { label: 'Privacy Policy', href: '/policies#privacy-policy' },
  { label: 'Terms & Conditions', href: '/policies#terms-and-conditions' },
  { label: 'Autopay Terms', href: '/policies#autopay' },
] as const;

/**
 * Form endpoints. Submissions post to the standalone Cloudflare Worker container
 * (https://lux-contact-proxy.bndl.workers.dev) which validates Turnstile and honeypot
 * before forwarding to LeadConnector webhooks stored securely in Worker environment
 * secrets. The raw LeadConnector URLs are never exposed in client code.
 */
export const WEBHOOKS = {
  contact: 'https://lux-contact-proxy.bndl.workers.dev',
  dataRoom: 'https://lux-contact-proxy.bndl.workers.dev?action=data-room',
} as const;

/** Cloudflare Turnstile configuration */
export const TURNSTILE = {
  siteKey: '0x4AAAAAAEuWPofkFPZnpnRS',
} as const;


export const QUOTES = {
  cfoEnergy: {
    text: '"As the world becomes increasingly AI-driven and energy-dependent, the demand for power over the next 5 to 10 years will surge. At Lux Financial, we\u2019re focused on being part of the solution, offering energy programs that not only address this growing need but also give investors the opportunity to participate in what we believe will become one of the world\u2019s most valuable currencies: energy."',
    name: 'Brennan Campbell',
    title: 'Chief Financial Officer, Lux Financial',
  },
  presidentPlanning: {
    text: '"Too many clients reach the end of the year realizing they\u2019re stuck with taxes they could have planned around. We work with advisors early to make sure that doesn\u2019t happen."',
    name: 'Josh Fifita',
    title: 'President, Lux Financial',
  },
  ceoCulture: {
    text: '"We\u2019ve built a culture centered on collaboration, working hand-in-hand with clients\u2019 CPAs, wealth advisors, and family offices. Transparency isn\u2019t just a value, it\u2019s how we operate every day."',
    name: 'Shawn Smart',
    title: 'Chief Executive Officer, Lux Financial',
  },
} as const;

export const PRESS_LOGOS = [
  {
    src: 'images/press-business-insider.png',
    alt: 'Business Insider',
    width: 768,
    height: 243,
    href: '/article-2b-offset',
    linkLabel: 'Read our Business Insider feature',
  },
  { src: 'images/press-yahoo.png', alt: 'Yahoo', width: 243, height: 102 },
  {
    src: 'images/press-ceo-weekly.png',
    alt: 'CEO Weekly',
    width: 214,
    height: 61,
  },
  {
    src: 'images/press-apple-news.png',
    alt: 'Apple News+',
    width: 266,
    height: 60,
  },
  {
    src: 'images/press-associated-press.png',
    alt: 'Associated Press',
    width: 326,
    height: 72,
  },
] as const;
